/**
 * Fondation feature flag gating the specialist's views: « Élèves suivis » (#1226), where the
 * « Intervenant » named on an enrolment (#824) — an orthopédagogue, a psychoéducateur — reads the
 * competency progress, hours and overrun of the students attached to them, and since #1445 their
 * whole section, « Suivi spécialisé »: the periods booked with them, their calendar, and the
 * groups of their students in consultation. Toggled by an administrator at `/core/features`; the
 * declaration lives in `backend/Parcours.Core/Features/SpecialistViewFeature.cs`, and this key must
 * match its `KEY` exactly — a key the catalog does not know reads as `false`, which HIDES the view.
 *
 * Declared `DefaultEnabled = false`: the view opens students to people who do not teach them, which
 * a centre decides for itself. It is therefore read strictly (`=== true`) through
 * {@link specialistViewAccess}, never through `isEnabledUnlessTurnedOff`. The section and every
 * route of it carry the flag but its dashboard, which says itself that the view is off.
 *
 * Kept free of imports: routes.ts, sidebar-items.ts and utils/landing.ts all read it.
 */
export const SPECIALIST_VIEW_FEATURE = 'SpecialistView';

/**
 * `LearningModulesModule.PERMISSION_SPECIALIST_FOLLOW_UP` (« Suivi spécialisé »), carried by the
 * seeded « Spécialiste » role. On the server it opens the two follow-up routes, nothing else: no
 * student file, no teacher scope, no write.
 */
// i18n-ignore-next-line: permission key, a contract with LearningModulesModule.PERMISSION_SPECIALIST_FOLLOW_UP
export const PERMISSION_SPECIALIST_FOLLOW_UP = 'Parcours.LearningModule.SuiviSpécialisé';

/**
 * « Élèves suivis »: a menu entry of the specialist's section and the detail's first crumb. Kept
 * under /enseignement since #1226 — its route, its name and its help key are contracts — though
 * the entry moved to the specialist's own section (#1445).
 */
export const FOLLOWED_STUDENTS_PATH = '/enseignement/eleves-suivis';

/** The specialist's own section, « Suivi spécialisé » (#1445): its prefix and its pages. */
export const SPECIALIST_SECTION_PATH = '/suivi';
/** The section's dashboard, where a specialist-only account lands. */
export const SPECIALIST_DASHBOARD_PATH = '/suivi/tableau-de-bord';
/** Their own Présences sheet: the periods booked with them. */
export const SPECIALIST_SHEET_PATH = '/suivi/presences';
/** Their calendar of appointments. */
export const SPECIALIST_CALENDAR_PATH = '/suivi/calendrier';
/** Groupe › Présences, in consultation: the week of a group of their students. */
export const SPECIALIST_GROUP_ATTENDANCE_PATH = '/suivi/assiduite';
/** Groupe › Progression, in consultation. */
export const SPECIALIST_GROUP_PROGRESSION_PATH = '/suivi/progression-groupe';
/** Gestion › Spécialistes: where the office registers and manages the specialists. */
export const SPECIALISTS_MANAGEMENT_PATH = '/gestion/specialistes';

/**
 * What the follow-up pages may show the reader:
 *
 * - `pending` — the boot is still running: the signed-in user or the flags are not known yet, and
 *   reading the empty flag map now would flash « Fonctionnalité désactivée »;
 * - `refused` — the reader does not hold the permission (or the boot ended with no user at all);
 * - `unavailable` — the boot is OVER with the flag map still empty: the flag load failed, so
 *   waiting would spin forever; the page offers to load the flags again;
 * - `disabled` — the flags are in and the view is not on;
 * - `granted` — the follow-up.
 */
export type SpecialistViewAccess = 'pending' | 'unavailable' | 'disabled' | 'refused' | 'granted';

/**
 * Reads {@link SpecialistViewAccess}.
 *
 * @param flags `useSdk().Features.flags.value` — pass the ref's value so the reading stays reactive.
 * @param permissions the signed-in user's permissions, `undefined` until `me` resolves.
 * @param initialized `useSdk().initialized.value` — true once the SDK boot, flag load included, is over.
 */
export function specialistViewAccess(
  flags: Record<string, boolean>,
  permissions: readonly string[] | null | undefined,
  initialized: boolean,
): SpecialistViewAccess {
  if (!permissions) return initialized ? 'refused' : 'pending';
  if (!permissions.includes(PERMISSION_SPECIALIST_FOLLOW_UP)) return 'refused';
  if (Object.keys(flags).length === 0) return initialized ? 'unavailable' : 'pending';
  return flags[SPECIALIST_VIEW_FEATURE] === true ? 'granted' : 'disabled';
}
